import React from 'react';
import {Box, Flex, useTheme, VStack, HStack, Text, Divider} from '@chakra-ui/react';
import {motion} from 'framer-motion';
import {TextA16, TextA14, TextA18, TextG20, TextG14, TextG16} from '../typography';
import styled from 'styled-components';
import {Offer} from '../../types/article';
import {useNavigate} from 'react-router-dom';
import Icon from '../Icon';
import GetMembership from '../GetMembership';
import {BlueButton} from '../button';
import dayjs from 'dayjs';

interface PerksButtonDarkAltProps {
  offers: Offer[];
  perksCount?: number;
  user?: any;
  nextMonthOffers?: Offer[];
  nextMonthPerksCount?: number;
  totalRetailValue?: number;
  nextMonthRetailValue?: number;
}

const MainCard = styled(motion.div)`
  width: 100%;
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
  cursor: pointer;
  background: #fff;
  border: 2px solid #f4cfe2;
  user-select: none;
  -webkit-user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.99);
  }
`;

const OfferCircle = styled(motion.div)`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  position: relative;
  flex-shrink: 0;
  margin-left: -10px;
  background: #f0f0f0;

  &:first-child {
    margin-left: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const MoreCircle = styled(motion.div)<{$bgColor?: string}>`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  position: relative;
  flex-shrink: 0;
  margin-left: -10px;
  background: ${(props) => props.$bgColor || '#F4CFE2'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c9136b;
  font-weight: 700;
  font-size: 16px;
  font-family: Gordita;
`;

const SecondaryRow = styled(motion.div)`
  width: 100%;
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
  cursor: pointer;
  background: transparent;
  border: none;
  user-select: none;
  -webkit-user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.99);
  }
`;

const PerksButtonDarkAlt: React.FC<PerksButtonDarkAltProps> = ({
  offers,
  perksCount,
  user,
  nextMonthOffers = [],
  nextMonthPerksCount,
  totalRetailValue = 0,
  nextMonthRetailValue = 0,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const actualPerksCount = perksCount || offers.length;
  const actualNextMonthPerksCount = nextMonthPerksCount || nextMonthOffers.length;

  const isProduction = process.env.NODE_ENV === 'production';
  const insiderClubInterestId = isProduction ? '294' : '212';

  // Use the backend-calculated values passed as props
  const totalRetailPrice = totalRetailValue;
  const nextMonthRetailPrice = nextMonthRetailValue;

  const handleNavigate = () => {
    navigate(`/monthly-perks/current`);
  };

  const handleNextMonthNavigate = () => {
    navigate(`/monthly-perks/next`);
  };

  const handleMeetupNavigate = () => {
    navigate('/membership/2');
  };

  if (!user?.hasActiveMembership) {
    return null;
  }

  // Show up to 3 offer images, then +N for remaining
  const maxVisibleOffers = 3;
  const visibleOffers = offers.slice(0, maxVisibleOffers);
  const remainingCount = Math.max(0, actualPerksCount - maxVisibleOffers);

  return (
    <Box
      borderRadius="0 0 20px 20px"
      bg="#FAE7F0"
      p={4}
      pt="40px"
      pb={4}
      mx={-4}
      mb="20px"
      position="relative"
      userSelect="none"
      css={`
        -webkit-user-select: none;
        -webkit-tap-highlight-color: transparent;
      `}>
      {/* Header */}
      <TextG20 color="#202020" mb={4} fontWeight="700">
        Your Insiders Club Benefits
      </TextG20>

      <VStack width="100%" spacing={3} align="center">
        {/* Main Perks Card */}
        <MainCard
          onClick={handleNavigate}
          whileTap={{scale: 0.98}}
          transition={{type: 'spring', stiffness: 400, damping: 25}}>
          <Flex align="center" justify="space-between">
            {/* Left side: Value + Label */}
            <Box flexShrink={0}>
              <Flex align="baseline">
                <TextG20 fontSize="22px" fontWeight="700" color="#202020" lineHeight="32px">
                  ${Math.floor(totalRetailPrice)}
                </TextG20>
                <TextG14 fontWeight="500" color="#4D4D4D" ml={1}>
                  value
                </TextG14>
              </Flex>
              <TextG14 fontWeight="700" color="#202020" mt={1}>
                {dayjs().format('MMMM')} Exclusives
              </TextG14>
            </Box>

            {/* Middle: Offer preview circles */}
            <Flex align="center" flex={1} justify="flex-end" ml={3} mr={2}>
              <HStack spacing={0}>
                {visibleOffers.map((offer, index) => (
                  <OfferCircle
                    key={offer.id}
                    initial={{scale: 0.8, opacity: 0}}
                    animate={{scale: 1, opacity: 1}}
                    transition={{delay: index * 0.05}}>
                    {offer.image_url ? (
                      <img src={offer.image_url} alt={offer.title || 'Offer'} />
                    ) : (
                      <Box w="100%" h="100%" bg="gray.200" />
                    )}
                  </OfferCircle>
                ))}
                {remainingCount > 0 && (
                  <MoreCircle
                    $bgColor="#F4CFE2"
                    initial={{scale: 0.8, opacity: 0}}
                    animate={{scale: 1, opacity: 1}}
                    transition={{delay: 0.15}}>
                    +{remainingCount}
                  </MoreCircle>
                )}
              </HStack>
            </Flex>

            {/* Right arrow */}
            <Icon
              iconName="fi fi-rr-angle-small-right"
              style={{
                color: '#aaa',
                fontSize: '20px',
                flexShrink: 0,
              }}
            />
          </Flex>
        </MainCard>

        {/* Preview Next Month Row */}
        {actualNextMonthPerksCount > 0 && nextMonthRetailPrice > 0 && (
          <SecondaryRow
            onClick={handleNextMonthNavigate}
            whileTap={{scale: 0.98}}
            transition={{type: 'spring', stiffness: 400, damping: 25}}>
            <Flex align="center" gap={2}>
              <Icon
                iconName="fi fi-sr-lock"
                style={{
                  color: '#1a1a1a',
                  fontSize: '14px',
                  width: '14px',
                  height: '14px',
                }}
              />
              <TextG14 fontWeight="600" color="#1a1a1a">
                Preview next month
              </TextG14>
            </Flex>
            <Flex align="center" gap={1}>
              <Flex>
                <TextG16 fontWeight="700" color="#202020">
                  ${Math.floor(nextMonthRetailPrice)}
                </TextG16>
                <TextG14 fontWeight="500" color="#4D4D4D" ml={1}>
                  value so far
                </TextG14>
              </Flex>
              <Icon
                iconName="fi fi-rr-angle-small-right"
                style={{
                  color: '#aaa',
                  fontSize: '18px',
                  width: '18px',
                  height: '18px',
                }}
              />
            </Flex>
          </SecondaryRow>
        )}

        <Divider borderColor="#E1E1E1" mt="0 !important" />

        {/* Bottom CTA Button */}
        <BlueButton onClick={handleMeetupNavigate} mt={1}>
          See All Benefits
        </BlueButton>
      </VStack>
    </Box>
  );
};

export default PerksButtonDarkAlt;
